import { Injectable } from "@angular/core";
import { EventFormParentEventData } from "../../admin/events-screen/event-page-models/event-form-parent-event-data";
import {
  ParentQuestion,
  ParentQuestionType,
} from "../../admin/events-screen/event-page-models/parent-question";
import { NoalGlobalData } from "./events.service";
import {
  Circle,
  County,
  Layer,
  Nest,
} from "../../admin/events-screen/event-page-models/noal.models";
import { PostEventModel } from "../../admin/events-screen/event-page-models/post-event-model";

import { EventFormInitialData } from "../../admin/events-screen/event-page-models/event-form-initial-data";
import * as moment from "moment";
import { EventFormNoalDataKehilot } from "../../admin/events-screen/event-page-models/event-form-noal-data-kehilot";
import { deepClone } from "../../_helpers/deepClone";
import { EventCollectionItem } from "../../admin/events-screen/event-page-models/event-collection-item";
import { SelectMenuOption } from "../../form/select-menu/select-menu.component";

export interface ParentEventDataFromTheServer {
  questions: Array<{
    previous_content: string;
    content: string;
    answers: Array<{
      previous_content: string;
      content: string;
    }>;

    question_type: string;
  }>;
  budget_item_line: {
    id: number;
    description: string;
  };
  id: number;
  name: string;
  event_start_date: number;
  event_end_date: number;
  registration_start_date: number;
  registration_end_date: number;
  description: string;
  tax_payers_only: boolean;
  has_brother_discount: boolean;
  code: string;
  priority_index: string;
  merkaz_revach_1: string;
  merkaz_revach_3: string;
  direct_permission_users: Array<{
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  }>;
  child_events: Array<{
    id: number;
    price: number;
    can_be_deleted: boolean;
    kehilot: [
      {
        id: number;
        name: string;
        is_selected: boolean;
        has_add_permission: boolean;
        mehozot: [
          {
            id: number;
            name: string;
            is_selected: boolean;
            has_add_permission: boolean;
            kenim: [
              {
                id: number;
                is_selected: boolean;
                has_add_permission: boolean;
                name: string;
              }
            ];
          }
        ];
      }
    ];
    grades: [
      {
        id: number;
        name: string;
        is_selected: boolean;
      }
    ];
    price_after_increase: number;
    price_increase_date: number;
    description: string;
    code: string;
    event_start_date: number;
    event_end_date: number;
    registration_start_date: number;
    registration_end_date: number;
    tax_payers_only: true;
    has_brother_discount: true;
    event_collections: Array<{
      name: string;
      id: number;
      is_selected: boolean;
    }>;
  }>;
  child_events_to_delete: number[];

  event_collections: Array<{ name: string; id: number; is_selected: boolean }>;
}

@Injectable()
export class EventSerializerService {
  public static readonly questionIdHash = "question#Id_";

  public static removeFieldsFromClonedEvent(
    eventDataFromServerRef: EventFormParentEventData
  ) {
    const eventDataFromServeCloned = deepClone(eventDataFromServerRef);
    eventDataFromServeCloned.id = 0;
    eventDataFromServeCloned.code = "";
    eventDataFromServeCloned.merkaz_revach_1 = "";
    eventDataFromServeCloned.merkaz_revach_3 = "";
    eventDataFromServeCloned.priority_index = "";
    eventDataFromServeCloned.child_events.forEach((childEvent) => {
      childEvent.id = 0;
      childEvent.code = "";
    });
    eventDataFromServeCloned.questions.forEach((q) => {
      q.id = "";
    });
    return eventDataFromServeCloned;
  }

  public static createNewQuestionId = () =>
    EventSerializerService.questionIdHash +
    // tslint:disable-next-line:no-bitwise
    ((Math.random() * 10000) | 0).toString();
  public static serializeParentEventDataForEditAndCreate(
    eventFormParentEventData: ParentEventDataFromTheServer
  ): EventFormParentEventData {
    const parentQuestions: ParentQuestion[] = [];
    eventFormParentEventData.questions.forEach((q, i) => {
      parentQuestions.push(
        EventSerializerService.serializeParentQuestionFromServerToClient(q)
      );
    });

    return {
      budget_item_line: {
        id:
          (eventFormParentEventData.budget_item_line &&
            eventFormParentEventData.budget_item_line.id) ||
          0,
        description:
          (eventFormParentEventData.budget_item_line &&
            eventFormParentEventData.budget_item_line.description) ||
          "",
      },
      child_events: eventFormParentEventData.child_events,
      code: eventFormParentEventData.code,
      description: eventFormParentEventData.description,
      direct_permission_users: [],
      event_end_date: eventFormParentEventData.event_end_date,
      event_start_date: eventFormParentEventData.event_start_date,
      has_brother_discount: eventFormParentEventData.has_brother_discount,
      id: eventFormParentEventData.id,
      merkaz_revach_1: eventFormParentEventData.merkaz_revach_1,
      merkaz_revach_3: eventFormParentEventData.merkaz_revach_3,
      name: eventFormParentEventData.name,
      priority_index: eventFormParentEventData.priority_index,
      registration_end_date: eventFormParentEventData.registration_end_date,
      registration_start_date: eventFormParentEventData.registration_start_date,
      tax_payers_only: eventFormParentEventData.tax_payers_only,
      questions: parentQuestions,
      event_collections: eventFormParentEventData.event_collections,
      child_events_to_delete: [],
    };
  }
  public static eventCollectionItemToSelectMenuOption(
    eventCollection: EventCollectionItem
  ): SelectMenuOption {
    return {
      key: eventCollection.id.toString(),
      text: eventCollection.name,
      customValue: "",
    };
  }
  public static serverEventCollectionItemToSelectMenuOption(eventCollection: {
    name: string;
    id: number;
    is_selected: boolean;
  }): SelectMenuOption {
    return {
      key: eventCollection.id.toString(),
      text: eventCollection.name,
      customValue: "",
    };
  }
  public static serializeParentQuestionFromServerToClient(serverQuestion: {
    previous_content: string;
    content: string;
    answers: Array<{
      previous_content: string;
      content: string;
    }>;
    question_type: string;
  }): ParentQuestion {
    const parentQuestion: ParentQuestion = {
      answer: "",
      id:
        serverQuestion.previous_content ||
        EventSerializerService.createNewQuestionId(),
      isRequired: false,
      options: serverQuestion.answers.map((a, i) => ({
        value: false,
        label: a.content,
        order: i,
        id: a.previous_content,
      })),
      order: 0,
      questionsType: ParentQuestionType.Text,
      subTitle: "",
      title: serverQuestion.content,
    };
    switch (serverQuestion.question_type) {
      case "multiple_options":
        parentQuestion.questionsType = ParentQuestionType.Dropdown;
        break;
      case "single_option":
        parentQuestion.questionsType = ParentQuestionType.CheckBox;
        break;
      case "short_question":
        parentQuestion.questionsType = ParentQuestionType.Text;
        break;
    }
    return parentQuestion;
  }
  public static serializeServerDataToNoalGlobalData(
    kehilot: Array<{
      id: number;
      name: string;
      has_add_permission: boolean;
      mehozot: Array<{
        id: number;
        name: string;
        has_add_permission: boolean;
        kenim: Array<{
          id: number;
          name: string;
          has_add_permission: boolean;
        }>;
      }>;
    }>,
    grades: Array<{
      id: number;
      name: string;
    }>,
    ken_collections: Array<{
      id: number;
      kenim: number[];
      name: string;
      is_kehila: boolean;
    }>
  ): NoalGlobalData {
    const noalGlobalData: NoalGlobalData = {
      circles: [],
      counties: [],
      layers: [],
      nests: [],
      ken_collections,
    };
    kehilot.forEach((kehila) => {
      const circle: Circle = {
        circleId: kehila.id,
        code: kehila.id.toString(),
        name: kehila.name,
        has_add_permission: kehila.has_add_permission !== false,
      };
      noalGlobalData.circles.push(circle);
      kehila.mehozot.forEach((mahoz) => {
        const county: County = {
          code: mahoz.id.toString(),
          countyId: mahoz.id,
          name: mahoz.name,
          parent: circle.circleId,
          has_add_permission: mahoz.has_add_permission !== false,
        };
        noalGlobalData.counties.push(county);
        mahoz.kenim.forEach((ken) => {
          const nest: Nest = {
            code: ken.id.toString(),
            name: ken.name,
            nestId: ken.id,
            parent: county.countyId,
            has_add_permission: ken.has_add_permission !== false,
          };
          noalGlobalData.nests.push(nest);
        });
      });
    });
    grades.forEach((grade) => {
      noalGlobalData.layers.push({ layerId: grade.id, layerName: grade.name });
    });
    return noalGlobalData;
  }
  public static dateToEpoc(date: string) {
    return new Date(date || null).getTime() / 1000;
  }
  public static EpocToDateAndTime(date: number): {
    datePicker: string;
    hoursPicker: string;
  } {
    const dateObj = new Date(date * 1000);

    if (!date) {
      return {
        datePicker: "",
        hoursPicker: "",
      };
    }

    return {
      datePicker: dateObj.toISOString().split("T")[0],
      hoursPicker: moment.utc(dateObj).format("HH:mm"),
    };
  }
  public static timeToEpoc(hoursAndMinuets: string) {
    if (!hoursAndMinuets || !hoursAndMinuets.includes(":")) {
      return 0;
    }
    const hoursMinutesArray = hoursAndMinuets.split(":");
    const hours = hoursMinutesArray[0] || null;
    const minutes = hoursMinutesArray[1] || null;

    if (!minutes || !hours) {
      return 0;
    }
    return (
      +hours * 60 * 60 + +minutes * 60 //+ new Date().getTimezoneOffset() * 60
    );
  }
  public static removeEmptyFieldsFromEventPost(
    postEventForm: PostEventModel
  ): Partial<PostEventModel> {
    if (!postEventForm.registration_period) {
      delete postEventForm.registration_period;
    }
    if (!postEventForm.priority_index) {
      delete postEventForm.priority_index;
    }
    if (!postEventForm.merkaz_revach_1) {
      delete postEventForm.merkaz_revach_1;
    }
    postEventForm.child_events.forEach((childEvent) => {
      if (!childEvent.registration_period) {
        delete childEvent.registration_period;
      }
      if (
        !childEvent.price_increase_date &&
        +childEvent.price_increase_date !== 0
      ) {
        delete childEvent.price_increase_date;
      }

      if (!+childEvent.registration_start_date) {
        delete childEvent.registration_start_date;
      }
      if (!+childEvent.registration_end_date) {
        delete childEvent.registration_end_date;
      }
    });

    return postEventForm;
  }
  public static addMissingOrgansToEventForm(
    eventForm: PostEventModel,
    allInitialData: EventFormInitialData
  ): PostEventModel {
    eventForm.child_events.forEach((childEvent, index) => {
      const kehilot = allInitialData.eventFormParentEventData.child_events[
        index
      ]
        ? EventSerializerService.mergeUserKehilotWithFormKehilot(
            JSON.parse(
              JSON.stringify(allInitialData.eventFormNoalData.kehilot)
            ),
            allInitialData.eventFormParentEventData.child_events[index].kehilot
          )
        : allInitialData.eventFormNoalData.kehilot;
      childEvent.kenim.forEach((kenId) => {
        kehilot.forEach((circleData) => {
          circleData.mehozot.forEach((mahozData) => {
            mahozData.kenim.forEach((kenInMahoz) => {
              if (
                kenInMahoz.id === kenId &&
                !childEvent.mehozot.includes(mahozData.id)
              ) {
                childEvent.mehozot.push(mahozData.id);
              }
            });
          });
        });
      });
      childEvent.mehozot.forEach((mahoz) => {
        kehilot.forEach((circleData) => {
          circleData.mehozot.forEach((mahozData) => {
            if (
              mahozData.id === mahoz &&
              !childEvent.kehilot.includes(circleData.id)
            ) {
              childEvent.kehilot.push(circleData.id);
            }
          });
        });
      });
    });

    return eventForm;
  }
  public static mergeUserKehilotWithFormKehilot(
    userKehilot: EventFormNoalDataKehilot,
    eventKehilot: EventFormNoalDataKehilot
  ) {
    eventKehilot.forEach((eventKehila) => {
      const matchingUserKehila = userKehilot.find(
        (k) => k.id === eventKehila.id
      );
      if (!matchingUserKehila) {
        userKehilot.push(eventKehila);
        return;
      }
      eventKehila.mehozot.forEach((eventMahoz) => {
        const matchingUserMahoz = matchingUserKehila.mehozot.find(
          (m) => m.id === eventMahoz.id
        );
        if (!matchingUserMahoz) {
          matchingUserKehila.mehozot.push(eventMahoz);
          return;
        }
        eventMahoz.kenim.forEach((eventKen) => {
          const matchingUserKen = matchingUserMahoz.kenim.find(
            (k) => k.id === eventKen.id
          );
          if (!matchingUserKen) {
            matchingUserMahoz.kenim.push(eventKen);
          }
        });
      });
    });
    return userKehilot;
  }
  public static serializeEventFormToServerPost(eventForm: any) {
    const dateTimeToEpoc = (date: string, time: string) =>
      EventSerializerService.dateToEpoc(date) +
      EventSerializerService.timeToEpoc(time);

    const serverPost: PostEventModel = {
      event_collections: (
        eventForm.parentEventMain.parentEventDetails.eventCollection || []
      )
        .filter((c) => c)
        .map((collection) => Number(collection.key))
        .filter((c) => c),
      child_events: eventForm.AllChildEvents.ActiveChildEvents.map(
        (childEv) => {
          return {
            id: childEv.id,

            code: childEv.childEventAdvancedDetails.eventCode || "",
            description: childEv.childEventAdvancedDetails.eventDescription,
            event_end_date: dateTimeToEpoc(
              childEv.childEventAdvancedDetails.eventEndDate,
              childEv.childEventAdvancedDetails.eventEndHour
            ).toString(),
            event_start_date: dateTimeToEpoc(
              childEv.childEventAdvancedDetails.eventStartDate,
              childEv.childEventAdvancedDetails.eventStartHour
            ).toString(),
            grades: childEv.layers
              .filter((l) => l.chosen)
              .map((l) => l.layerId),
            has_brother_discount:
              !!childEv.childEventAdvancedDetails.brothersDiscountChildEvent,
            kehilot: childEv.circles
              .filter((circle) => circle.chosen)
              .map((circle) => circle.circleId),
            kenim: childEv.organs
              .filter((org) => org.nestOrCounty === "nest")
              .map((org) => org.id),
            mehozot: childEv.organs
              .filter((org) => org.nestOrCounty === "county")
              .map((org) => org.id),
            price: childEv.eventPrice,
            price_after_increase: childEv.priceChanges[0]
              ? childEv.priceChanges[0].priceChangeNewPrice
              : 0,
            price_increase_date: childEv.priceChanges[0]
              ? dateTimeToEpoc(
                  childEv.priceChanges[0].priceChangeDate,
                  ""
                ).toString()
              : "0",
            registration_end_date: dateTimeToEpoc(
              childEv.childEventAdvancedDetails.registrationEndDate,
              ""
            ).toString(),
            registration_period: 0,
            registration_start_date: dateTimeToEpoc(
              childEv.childEventAdvancedDetails.registrationStartDate,
              ""
            ).toString(),
            tax_payers_only:
              !!childEv.childEventAdvancedDetails.closedToMembersChildEvent,
            event_collections: (
              childEv.childEventAdvancedDetails.eventCollection || []
            ).map((ev) => +ev.key),
          };
        }
      ),
      child_events_to_delete: eventForm.AllChildEvents.DeletedChildEvents || [],
      budget_item_line: eventForm.parentEventBudgetSection.budgetSectionId,
      code:
        eventForm.parentEventMain.parentEventAdvancedDetails.eventCode || "",
      description:
        eventForm.parentEventMain.parentEventDetails.eventDescription,
      direct_permission_users: eventForm.parentEventAllPermittedUsers.map(
        (u) => u.id
      ),
      event_end_date: dateTimeToEpoc(
        eventForm.parentEventMain.parentEventDetails.eventEndDate,
        eventForm.parentEventMain.parentEventDetails.eventEndHour
      ).toString(),
      event_start_date: dateTimeToEpoc(
        eventForm.parentEventMain.parentEventDetails.eventStartDate,
        eventForm.parentEventMain.parentEventDetails.eventStartHour
      ).toString(),
      event_type: eventForm.parentEventMain.parentEventDetails.eventType
        ? +eventForm.parentEventMain.parentEventDetails.eventType.key
        : 0,
      has_brother_discount:
        !!eventForm.parentEventMain.parentEventDetails.brothersDiscount,
      merkaz_revach_1:
        eventForm.parentEventMain.parentEventAdvancedDetails.gainCenter1,
      merkaz_revach_3:
        eventForm.parentEventMain.parentEventAdvancedDetails.gainCenter3,
      name: eventForm.parentEventMain.parentEventDetails.eventName,
      priority_index:
        eventForm.parentEventMain.parentEventAdvancedDetails.eventIndex,
      questions: eventForm.parentEventParentQuestions.map((question) => {
        const questionObject: any = {
          // @ts-ignore
          answers: (question.options || []).map((option) => {
            const ans: any = {
              content: option.label,
            };
            if (
              option.id &&
              !option.id.includes(EventSerializerService.questionIdHash)
            ) {
              // ans.previous_content = option.id;
            }
            return ans;
          }),
          content: question.title,
          order: question.order,
        };

        if (question.questionsType === ParentQuestionType.CheckBox) {
          questionObject.answers = [questionObject.answers[0]];
        } else if (question.questionsType === ParentQuestionType.Text) {
          questionObject.answers = [];
        }
        if (
          question.id &&
          !question.id.includes(EventSerializerService.questionIdHash)
        ) {
          // questionObject.previous_content = question.id;
        }
        return questionObject;
      }),

      registration_end_date: dateTimeToEpoc(
        eventForm.parentEventMain.parentEventDetails.registrationEndDate,
        ""
      ).toString(),
      registration_period: eventForm.parentEventMain.parentEventDetails
        .brothersDiscountPeriod
        ? +eventForm.parentEventMain.parentEventDetails.brothersDiscountPeriod
            .key
        : 0,
      registration_start_date: dateTimeToEpoc(
        eventForm.parentEventMain.parentEventDetails.registrationStartDate,
        ""
      ).toString(),
      tax_payers_only:
        !!eventForm.parentEventMain.parentEventDetails.closedToMembers,
    };
    serverPost.child_events.forEach((childEvent) => {
      if (!childEvent.id || +childEvent.id <= 0) {
        delete childEvent.id;
      }
    });

    return serverPost;
  }
}



// WEBPACK FOOTER //
// ./src/app/_services/eventsServices/eventSerializer.service.ts